import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
// Utils
import RealtimeUtil from '../utils/RealtimeUtil';
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
import i18n from '../locales/i18n';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class MarkersService {
    static addMarker(marker, action) {
        marker.id = uuidv4();
        marker.properties.markerId = marker.id;
        const promise = Axios.post(BACKEND_ENDPOINT + 'markers/' + RealtimeUtil.getConnectionId(), marker, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            marker.properties = response.data.properties;
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'marker' + (action === 'copyPasting' ? 'copy_pasting' : action); // TODO Repère : vérifier toasts existent
        const successId = 'marker' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'marker' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise);
        else showToast('will_sync');
        return promise;
    }

    static addMarkers(markers, action) {
        markers.forEach(m => {
            m.id = uuidv4();
            m.properties.markerId = m.id;
        });
        const promise = Axios.post(BACKEND_ENDPOINT + 'markers/bulk/' + RealtimeUtil.getConnectionId(), markers, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            response.data.forEach(m => {
                let marker = markers.find(x => x.id === m.id);
                marker.properties = m.properties;
            });
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefix = markers.length > 1 ? 'markers_' : 'marker_'; // TODO Repère : ajouter toasts
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise, markers.length);
        else showToast('will_sync');
        return promise;
    }

    static getMarkers(projectId, data) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/projects/' + projectId, data, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('markers_loading_failed');
            return;
        });
    }

    static removeMarkers(markers) {
        const promise = Axios.delete(BACKEND_ENDPOINT + 'markers/' + RealtimeUtil.getConnectionId(), {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: markers
        }).then(response => {
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefixe = markers.length > 1 ? 'markers' : 'marker';
        if (navigator.onLine) showLoadingToast(prefixe + '_deleting', prefixe + '_deleted', prefixe + '_deletion_failed', promise);
        else showToast('will_sync');
        return promise;
    }

    static updateMarker(id, marker, action) {
        const promise = Axios.put(BACKEND_ENDPOINT + 'markers/?connectionId=' + RealtimeUtil.getConnectionId() + '&id=' + id, marker, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            marker.properties = { ...marker.properties, ...response.data.properties };
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        // TODO PolygonMarkers
        const toastId = 'marker_' + action;
        const successId = 'marker_' + action.replace('ing', 'ed');
        const errorId = 'marker_' + (action === 'updating' ? 'update' : action) + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise);
        else showToast('will_sync');
        return promise;
    }

    static bulkUpdateMarkers(markers, action) {
        const promise = Axios.put(BACKEND_ENDPOINT + 'markers/bulk/' + RealtimeUtil.getConnectionId(), markers, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (Array.isArray(response?.data)) {
                response.data.forEach(feature => {
                    let marker = markers.find(marker => marker.id === feature.id);
                    if (marker) marker.properties = { ...marker.properties, ...feature.properties };
                });
            }
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        // TODO PolygonMarkers
        const toastId = 'markers_' + action;
        const successId = 'markers_' + action.replace('ing', 'ed');
        const errorId = 'markers_' + (action === 'updating' ? 'update' : action) + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise);
        else showToast('will_sync');
        return promise;
    }

    static exportMarkersFromProjectAsExcel(name, id, elementIds = [], projection) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportXLSX/' + id, { elementIds, projection }, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id} - ${name} (${i18n.t("Repères").toLowerCase()}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
            return true;
        }, () => {
            if (navigator.onLine) showToast('elements_xlsx_export_failed');
        });
    }

    static exportMarkerAsPDF(marker, image, photosId, colorless, toastCustomTexts) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportPDF/' + marker.id, { image, photosId, colorless }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            let filename = marker.properties.projectReference ? `${i18n.t("Fiche repère")}_${marker.properties.projectReference}` : i18n.t("Fiche repère");
            if (marker.properties.customReference) filename += '_' + marker.properties.customReference;
            filename += '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (navigator.onLine) showToast('elements_pdf_export_failed');
        });
    }

    static exportMarkersAsPDF(projectId, elementIds, { projection, colorless = false, addPhotos = true } = {}) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportAllPDF/' + projectId, { elementIds, projection, colorless, addPhotos }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response;
        }, () => {
            if (navigator.onLine) showToast('elements_pdf_export_failed');
            return;
        });
    }

    static exportMarkersAsSHP(projectLabel, projectId, elementIds, projection, geometryType) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportSHP/' + projectId, { elementIds, projection, geometryType }, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (response.data?.size > 0) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Repères").toLowerCase()} - ${geometryType}).zip`);
                document.getElementById('modal-content').appendChild(link);
                link.click();
                document.getElementById('modal-content').removeChild(link);
                URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (navigator.onLine) showToast('elements_shp_export_failed');
        });
    }

    static exportMarkersAsGPKG(projectLabel, projectId, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportGPKG/' + projectId, { projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const blob = new Blob([response.data], { type: 'application/geopackage+sqlite3' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Repères").toLowerCase()}).gpkg`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (navigator.onLine) showToast('elements_gpkg_export_failed');
        });
    }
}
